import React, { useCallback, useEffect, useState } from "react";
import {
  Mail, RefreshCw, Loader2, CircleAlert, Link2, ShieldCheck, Eye, X, Calendar,
  FileEdit, ArrowUpRight, ChevronLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { openOAuthTab } from "@/lib/oauth";
import { openUrl } from "@/lib/outlook";
import { demoMessages } from "@/lib/msDemoData";
import { toast } from "sonner";
import MsReconnectBanner from "@/components/MsReconnectBanner";

const DEMO_KEY = "ph_ms_demo";

function isDemoOn() {
  try { return localStorage.getItem(DEMO_KEY) === "1"; } catch { return false; }
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z");
    return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

function ReadingPane({ demo, item, isDraft, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setData(null);
    if (demo) {
      // No live body in demo — render the preview text we already have.
      setData({
        id: item.id, subject: item.subject, from: item.from, from_email: item.from_email || "",
        to: item.to || [], cc: [], received: item.received,
        body_html: `<p>${(item.preview || "").replace(/</g, "&lt;")}</p>`, web_link: item.web_link,
      });
      setLoading(false);
      return () => { alive = false; };
    }
    api.get(`/integrations/microsoft/messages/${encodeURIComponent(item.id)}`)
      .then((r) => { if (alive) { setData(r.data); setLoading(false); } })
      .catch(() => {
        if (alive) {
          toast.error("Couldn't open that message.");
          setLoading(false);
        }
      });
    return () => { alive = false; };
  }, [demo, item]);

  return (
    <section className="panel inbox-reader" data-testid="inbox-reader">
      <div className="inbox-reader-head">
        <button className="btn btn-ghost inbox-reader-back" onClick={onClose} data-testid="inbox-reader-close">
          <ChevronLeft size={15} /> Back
        </button>
        {(data?.web_link || item.web_link) && (
          <button
            className="btn"
            onClick={() => openUrl(data?.web_link || item.web_link)}
            data-testid="inbox-reader-open-outlook"
            style={{ marginLeft: "auto" }}
          >
            <ArrowUpRight size={14} /> {isDraft ? "Edit in Outlook" : "Open in Outlook"}
          </button>
        )}
      </div>
      {loading ? (
        <div className="text-dim" style={{ fontSize: 13, padding: 24 }}>Loading message…</div>
      ) : data ? (
        <div className="inbox-reader-body" data-testid="inbox-reader-body">
          <h3 className="inbox-reader-subject" data-testid="inbox-reader-subject">{data.subject}</h3>
          <div className="inbox-reader-meta">
            {isDraft ? (
              <div><span className="text-dim">To: </span>{(data.to || []).join(", ") || "—"}</div>
            ) : (
              <div><span className="text-dim">From: </span>{data.from}{data.from_email ? ` <${data.from_email}>` : ""}</div>
            )}
            {data.received && <div className="text-dim" style={{ fontSize: 12 }}>{fmtTime(data.received)}</div>}
          </div>
          <div className="inbox-reader-html" dangerouslySetInnerHTML={{ __html: data.body_html || "<p class='text-dim'>(empty)</p>" }} />
        </div>
      ) : (
        <div className="text-dim" style={{ fontSize: 13, padding: 24 }}>Couldn't load this message.</div>
      )}
    </section>
  );
}

export default function Inbox() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(() => (isDemoOn() ? { configured: true, connected: false } : null));
  const [messages, setMessages] = useState(() => (isDemoOn() ? demoMessages() : []));
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(() => !isDemoOn());
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [demo, setDemo] = useState(isDemoOn);
  const [tab, setTab] = useState("inbox");
  const [selected, setSelected] = useState(null); // { item, isDraft }

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const m = await api.get("/integrations/microsoft/messages?top=25");
      setMessages(m.data.messages || []);
    } catch {
      toast.error("Couldn't read your Microsoft 365 email. Try reconnecting.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDrafts = useCallback(async () => {
    if (isDemoOn()) { setDrafts([]); return; }
    setDraftsLoading(true);
    try {
      const r = await api.get("/integrations/microsoft/drafts?top=25");
      setDrafts(r.data.drafts || []);
    } catch {
      toast.error("Couldn't read your Outlook drafts.");
    } finally {
      setDraftsLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    if (isDemoOn()) return;
    try {
      const r = await api.get("/integrations/microsoft/status");
      setStatus(r.data);
      if (r.data.connected) { await loadData(); loadDrafts(); }
      else setLoading(false);
    } catch {
      setLoading(false);
    }
  }, [loadData, loadDrafts]);

  useEffect(() => {
    const id = setTimeout(refresh, 0);
    return () => clearTimeout(id);
  }, [refresh]);

  const enableDemo = () => {
    try { localStorage.setItem(DEMO_KEY, "1"); } catch { /* ignore */ }
    setDemo(true);
    setStatus({ configured: true, connected: false });
    setMessages(demoMessages());
    setLoading(false);
    toast.message("Showing sample email — this is not a live Microsoft connection.");
  };

  const exitDemo = () => {
    try { localStorage.removeItem(DEMO_KEY); } catch { /* ignore */ }
    setDemo(false);
    setLoading(true);
    setMessages([]);
    setSelected(null);
    refresh();
  };

  const connect = async () => {
    setConnecting(true);
    const tabRef = openOAuthTab();
    try {
      const r = await api.get("/integrations/microsoft/connect");
      tabRef.go(r.data.authorize_url);
    } catch (e) {
      tabRef.fail();
      toast.error(e?.response?.data?.detail || "Microsoft 365 isn't configured yet.");
      setConnecting(false);
    }
  };

  const onSelectMail = (m) => setSelected({ item: m, isDraft: false });
  const onSelectDraft = (d) => setSelected({ item: d, isDraft: true });
  const switchTab = (t) => { setTab(t); setSelected(null); if (t === "drafts" && status?.connected) loadDrafts(); };

  const resync = () => { setSelected(null); refresh(); };

  const notConfigured = status && !status.configured && !demo;
  const notConnected = status && status.configured && !status.connected && !demo;
  const showMail = status?.connected || demo;

  return (
    <>
      <div className="surface-header">
        <div>
          <h2 style={{ margin: 0 }}>Inbox</h2>
          <div className="text-dim" style={{ fontSize: 13, marginTop: 2 }}>
            {demo
              ? "Sample email preview — not a live Microsoft 365 connection."
              : status?.connected
                ? `Reading ${status.email || "your mailbox"} — drafts only, never sends.`
                : "Your Microsoft 365 email, in one calm view."}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {showMail && (
            <button className="btn btn-ghost" onClick={() => navigate("/meetings")} data-testid="inbox-to-calendar-btn" title="Calendar lives in Meetings">
              <Calendar size={14} /> Calendar
            </button>
          )}
          {demo ? (
            <button className="btn" onClick={exitDemo} data-testid="inbox-exit-demo-btn">
              <X size={14} /> Exit preview
            </button>
          ) : status?.connected ? (
            <button className="btn" onClick={resync} disabled={loading} data-testid="inbox-refresh-btn">
              {loading ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />} Resync
            </button>
          ) : null}
        </div>
      </div>

      <MsReconnectBanner />

      {showMail && (
        <div className="inbox-tabs" data-testid="inbox-tabs">
          <button
            className={`inbox-tab ${tab === "inbox" ? "active" : ""}`}
            onClick={() => switchTab("inbox")}
            data-testid="inbox-tab-inbox"
          >
            <Mail size={14} /> Inbox
            <span className="inbox-tab-count">{messages.length}</span>
          </button>
          <button
            className={`inbox-tab ${tab === "drafts" ? "active" : ""}`}
            onClick={() => switchTab("drafts")}
            data-testid="inbox-tab-drafts"
          >
            <FileEdit size={14} /> Drafts
            {!demo && <span className="inbox-tab-count">{drafts.length}</span>}
          </button>
        </div>
      )}

      <div style={{ flex: 1, overflowY: "auto", padding: "24px clamp(14px, 4vw, 32px) 60px" }}>
        {loading && !status && (
          <div className="text-dim" style={{ fontSize: 13 }}>Checking your connection…</div>
        )}

        {notConfigured && (
          <div className="inbox-connect-card" data-testid="inbox-not-configured">
            <CircleAlert size={26} className="text-gold" />
            <h3 style={{ margin: "12px 0 6px" }}>Microsoft 365 isn’t set up yet</h3>
            <div className="text-soft" style={{ fontSize: 13.5, lineHeight: 1.6, maxWidth: 460 }}>
              An administrator needs to finish the Microsoft 365 connection in the workspace settings
              before employees can link their mailboxes.
            </div>
            <button className="btn" onClick={enableDemo} data-testid="inbox-demo-btn" style={{ marginTop: 16 }}>
              <Eye size={15} /> Preview with sample data
            </button>
          </div>
        )}

        {notConnected && (
          <div className="inbox-connect-card" data-testid="inbox-connect">
            <span className="inbox-ms-logo" aria-hidden="true">
              <svg width="34" height="34" viewBox="0 0 23 23">
                <rect x="1" y="1" width="10" height="10" fill="#F25022" />
                <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
                <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
                <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
              </svg>
            </span>
            <h3 style={{ margin: "14px 0 6px" }}>Connect your Microsoft 365</h3>
            <div className="text-soft" style={{ fontSize: 13.5, lineHeight: 1.6, maxWidth: 480 }}>
              Sign in with your work account so the platform can read your email and save AI-written
              email drafts to your Outlook Drafts. We never send anything on your behalf — drafts only.
            </div>
            <div className="inbox-scopes">
              <span><Mail size={13} /> Read mail</span>
              <span><FileEdit size={13} /> Save drafts</span>
              <span><ShieldCheck size={13} /> Never sends</span>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap", justifyContent: "center" }}>
              <button className="btn btn-primary" onClick={connect} disabled={connecting} data-testid="inbox-connect-btn">
                {connecting ? <Loader2 size={15} className="spin" /> : <Link2 size={15} />}
                {connecting ? "Redirecting…" : "Connect Microsoft 365"}
              </button>
              <button className="btn" onClick={enableDemo} data-testid="inbox-demo-btn">
                <Eye size={15} /> Preview with sample data
              </button>
            </div>
          </div>
        )}

        {demo && (
          <div className="inbox-demo-banner" data-testid="inbox-demo-banner">
            <Eye size={15} />
            <span>
              <strong>Sample email preview.</strong> These messages are illustrative —
              there is no live Microsoft 365 connection yet. Connect a real account to see your own mailbox and drafts.
            </span>
            <button className="btn btn-ghost" onClick={exitDemo} style={{ marginLeft: "auto" }}>Exit</button>
          </div>
        )}

        {showMail && (
          <div className={selected ? "inbox-split" : ""} data-testid="inbox-list-grid" style={selected ? {} : { maxWidth: 760, margin: "0 auto" }}>
            {/* List column */}
            {tab === "inbox" && (
              <section className="panel inbox-col" data-testid="inbox-mail">
                <div className="inbox-col-head">
                  <Mail size={15} className="text-cobalt" /> Recent email
                  <span className="text-dim" style={{ marginLeft: "auto", fontSize: 12 }}>{messages.length}</span>
                </div>
                {loading && <div className="text-dim" style={{ fontSize: 13, padding: 12 }}>Loading mail…</div>}
                {!loading && messages.length === 0 && (
                  <div className="text-dim" style={{ fontSize: 13, padding: 12 }}>No messages.</div>
                )}
                {messages.map((m) => (
                  <button
                    key={m.id}
                    className={`inbox-mail-item ${m.is_read ? "" : "unread"} ${selected?.item?.id === m.id ? "active" : ""}`}
                    onClick={() => onSelectMail(m)}
                    data-testid={`inbox-mail-${m.id}`}
                  >
                    <div className="inbox-mail-top">
                      <span className="inbox-mail-from">{m.from}</span>
                      <span className="inbox-mail-time">{fmtTime(m.received)}</span>
                    </div>
                    <div className="inbox-mail-subject">
                      {!m.is_read && <span className="inbox-dot" />}{m.subject}
                    </div>
                    <div className="inbox-mail-preview">{m.preview}</div>
                  </button>
                ))}
              </section>
            )}

            {tab === "drafts" && (
              <section className="panel inbox-col" data-testid="inbox-drafts">
                <div className="inbox-col-head">
                  <FileEdit size={15} className="text-cobalt" /> Outlook drafts
                  <span className="text-dim" style={{ marginLeft: "auto", fontSize: 12 }}>{drafts.length}</span>
                </div>
                {demo && (
                  <div className="text-dim" style={{ fontSize: 13, padding: 16, lineHeight: 1.6 }} data-testid="inbox-drafts-demo">
                    Drafts need a live Microsoft 365 connection. When connected, every email you approve
                    in Chat is saved here in your real Outlook Drafts folder.
                  </div>
                )}
                {!demo && draftsLoading && <div className="text-dim" style={{ fontSize: 13, padding: 12 }}>Loading drafts…</div>}
                {!demo && !draftsLoading && drafts.length === 0 && (
                  <div className="text-dim" style={{ fontSize: 13, padding: 16, lineHeight: 1.6 }} data-testid="inbox-drafts-empty">
                    No drafts yet. Ask the assistant to write an email, then press
                    <strong> Approve &amp; save to Outlook</strong> — it will appear here.
                  </div>
                )}
                {!demo && drafts.map((d) => (
                  <button
                    key={d.id}
                    className={`inbox-mail-item ${selected?.item?.id === d.id ? "active" : ""}`}
                    onClick={() => onSelectDraft(d)}
                    data-testid={`inbox-draft-${d.id}`}
                  >
                    <div className="inbox-mail-top">
                      <span className="inbox-mail-from">To: {(d.to || []).join(", ") || "—"}</span>
                      <span className="inbox-mail-time">{fmtTime(d.modified)}</span>
                    </div>
                    <div className="inbox-mail-subject">{d.subject}</div>
                    <div className="inbox-mail-preview">{d.preview}</div>
                  </button>
                ))}
              </section>
            )}

            {/* Reading pane */}
            {selected && (
              <ReadingPane
                demo={demo}
                item={selected.item}
                isDraft={selected.isDraft}
                onClose={() => setSelected(null)}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
}
